import { useMemo, useRef } from 'react';
import { InfiniteData, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { atom } from 'jotai';

import { IndexInvoices, useEmployersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 10;
export const fetchInvoicesQueryKey = ['invoices-list'];
export const useFetchInvoicesQuery = (params?: { limit?: number }) => {
  const { limit = DEFAULT_LIMIT } = params || {};
  const { user } = useAuth();
  const organizationId = user?.organization_id;
  const employersService = useEmployersService();
  const nextPageRef = useRef<number>();
  const hasNextPageRef = useRef<boolean>();

  const query = useInfiniteQuery({
    queryKey: fetchInvoicesQueryKey,
    queryFn: async ({ pageParam }) => {
      nextPageRef.current = pageParam + 1;
      return (
        await employersService.fetchInvoices({
          limit,
          offset: pageParam * limit,
          organizationId,
        })
      ).data;
    },
    getNextPageParam: (lastPage) => {
      const totalHits = lastPage.hits;
      const invoicesLoadedSoFar = nextPageRef.current * limit;

      hasNextPageRef.current = invoicesLoadedSoFar < totalHits;
      return nextPageRef.current;
    },
    refetchOnWindowFocus: false,
    initialPageParam: DEFAULT_OFFSET,
    enabled: !!organizationId,
  });
  const employerUserContactsInfo = useMemo(() => extractData(query.data), [query.data]);
  return {
    invoicesList: employerUserContactsInfo?.data,
    hits: employerUserContactsInfo?.hits,
    hasNextPage: hasNextPageRef.current,
    isLoading: query.isLoading,
    query,
  };
};

export function extractData(data?: InfiniteData<IndexInvoices, unknown>): IndexInvoices {
  if (!data || !data.pages) return { data: [], hits: 0 };
  return { data: data.pages.map((page) => page.data).flat(), hits: data.pages[0].hits };
}
export const invoiceIdAtom = atom<number>(null);
export const useDownloadInvoiceMutation = () => {
  const employersService = useEmployersService();
  return useMutation({
    mutationKey: ['download-invoice'],
    mutationFn: async ({ id }: { id?: number; invoiceNumber?: number }) =>
      (await employersService.fetchInvoicePdf({ invoiceId: id })).data,
    onSuccess(data, { invoiceNumber }) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `restworld-invoice-${invoiceNumber}.pdf`);
      // Force download
      link.click();
      // Clean up and remove the link
      link?.parentNode?.removeChild(link);
    },
    onError(error, variables) {
      console.error(`Error downloading invoice for ${variables.id}`, error);
    },
  });
};
